import { format, parseISO } from "date-fns"
import { Input } from "@/components/ui/input"

/**
 * The Date cell of a per-deduction credit memo line.
 *
 * Normally it just shows the line's execution date — the month the deduction belongs to, which is
 * also the month its Xero credit note books in. When that month is locked in Xero the note has to
 * post somewhere open instead, so the cell shows the original date struck through above an editable
 * posting date. Xero has no separate posting field, so the note's own date is the only lever; the
 * original date is written into the note's line description so it stays traceable inside Xero.
 */
export function PostingDateCell({
  execution_date,
  posting_date,
  first_open_date,
  onChange,
}: {
  execution_date?: string
  /** The date this note will post on, when that differs from `execution_date`. */
  posting_date?: string
  /** Earliest date Xero will accept, used to stop the picker offering a locked one. */
  first_open_date?: string
  onChange?: (posting_date: string) => void
}) {
  if (!execution_date) {
    return <span class="text-slate-700 tabular-nums whitespace-nowrap">—</span>
  }

  const original = format(parseISO(execution_date), "MMM d, yyyy")
  if (!posting_date) {
    return <span class="text-slate-700 tabular-nums whitespace-nowrap">{original}</span>
  }

  return (
    <div class="space-y-1">
      <div class="text-xs text-slate-400 line-through tabular-nums whitespace-nowrap" title="Period locked in Xero">
        {original}
      </div>
      <Input
        type="date"
        aria-label={`Posting date for the credit note originally dated ${original}`}
        class="h-8 w-36 text-xs tabular-nums"
        value={posting_date}
        min={first_open_date}
        onInput={(e: Event) => onChange?.((e.target as HTMLInputElement).value)}
      />
    </div>
  )
}
